import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface BasicPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  size?: number;
  totalElements?: number;
  first?: boolean;
  last?: boolean;
  onSizeChange?: (size: number) => void;
}

interface PageSizeSelectProps {
  value: number;
  onChange: (size: number) => void;
}

const DEFAULT_PAGE_SIZES = [5, 10, 20, 50, 100];

/**
 * Selector personalizado para cambiar el tamaño de página.
 */
const PageSizeSelect: React.FC<PageSizeSelectProps> = ({
  value,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  const options = useMemo(() => {
    if (DEFAULT_PAGE_SIZES.includes(value)) {
      return DEFAULT_PAGE_SIZES;
    }

    return [...DEFAULT_PAGE_SIZES, value].sort(
      (firstValue, secondValue) =>
        firstValue - secondValue,
    );
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (
      event: MouseEvent | TouchEvent,
    ) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(
          event.target as Node,
        )
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener(
      'mousedown',
      handleClickOutside,
    );

    document.addEventListener(
      'touchstart',
      handleClickOutside,
    );

    document.addEventListener(
      'keydown',
      handleEscape,
    );

    return () => {
      document.removeEventListener(
        'mousedown',
        handleClickOutside,
      );

      document.removeEventListener(
        'touchstart',
        handleClickOutside,
      );

      document.removeEventListener(
        'keydown',
        handleEscape,
      );
    };
  }, []);

  const handleSelect = (newValue: number) => {
    onChange(newValue);
    setIsOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full sm:w-auto"
    >
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        aria-label="Cambiar registros por página"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={`
          flex h-10 w-full min-w-[155px]
          items-center justify-between gap-3
          rounded-xl border bg-gray-1 px-3
          text-sm font-medium text-black
          outline-none transition-all duration-200
          hover:border-primary
          focus:border-primary
          focus:ring-2 focus:ring-primary/10
          dark:border-strokedark
          dark:bg-meta-4 dark:text-white
          sm:w-auto
          ${
            isOpen
              ? 'border-primary ring-2 ring-primary/10'
              : 'border-stroke'
          }
        `}
      >
        <span className="flex min-w-0 items-center gap-2">
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            className="shrink-0 text-body dark:text-bodydark2"
          >
            <path
              d="M8 6H21"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />

            <path
              d="M8 12H21"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />

            <path
              d="M8 18H21"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />

            <path
              d="M3 6H3.01"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            />

            <path
              d="M3 12H3.01"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            />

            <path
              d="M3 18H3.01"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </svg>

          <span className="truncate">
            {value} por página
          </span>
        </span>

        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          aria-hidden="true"
          className={`
            shrink-0 fill-current
            transition-transform duration-300
            ${
              isOpen
                ? 'rotate-180 text-primary'
                : 'rotate-0 text-body dark:text-bodydark2'
            }
          `}
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M5.29289 8.29289C5.68342 7.90237 6.31658 7.90237 6.70711 8.29289L12 13.5858L17.2929 8.29289C17.6834 7.90237 18.3166 7.90237 18.7071 8.29289C19.0976 8.68342 19.0976 9.31658 18.7071 9.70711L12.7071 15.7071C12.3166 16.0976 11.6834 16.0976 11.2929 15.7071L5.29289 9.70711C4.90237 9.31658 4.90237 8.68342 5.29289 8.29289Z"
          />
        </svg>
      </button>

      {isOpen && (
        <ul
          role="listbox"
          aria-label="Cantidad de registros por página"
          className="
            absolute bottom-full left-0 z-[99999]
            mb-2 max-h-60 w-full min-w-[155px]
            overflow-y-auto rounded-xl
            border border-stroke bg-white
            p-1.5 shadow-default
            dark:border-strokedark
            dark:bg-boxdark sm:left-auto sm:right-0
          "
        >
          {options.map((option) => {
            const isSelected = option === value;

            return (
              <li
                key={option}
                role="option"
                aria-selected={isSelected}
              >
                <button
                  type="button"
                  onClick={() => handleSelect(option)}
                  className={`
                    flex w-full items-center
                    justify-between gap-3 rounded-lg
                    px-3 py-2.5 text-left text-sm
                    transition-colors duration-150
                    ${
                      isSelected
                        ? `
                          bg-primary
                          font-medium text-white
                        `
                        : `
                          text-black
                          hover:bg-gray-2
                          hover:text-primary
                          dark:text-white
                          dark:hover:bg-boxdark-2
                        `
                    }
                  `}
                >
                  <span>{option} por página</span>

                  {isSelected && (
                    <svg
                      width="17"
                      height="17"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                      className="shrink-0"
                    >
                      <path
                        d="M5 12.5L9.2 16.5L19 7"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const BasicPagination: React.FC<
  BasicPaginationProps
> = ({
  currentPage,
  totalPages,
  onPageChange,
  size,
  totalElements,
  first,
  last,
  onSizeChange,
}) => {
  if (totalPages <= 1 && !onSizeChange) {
    return null;
  }

  const visiblePages = 5;

  const isFirstPage =
    first ?? currentPage <= 0;

  const isLastPage =
    last ??
    totalPages <= 0 ??
    currentPage >= totalPages - 1;

  const getPageNumbers = () => {
    if (totalPages <= 0) {
      return [];
    }

    let start = Math.max(
      1,
      currentPage +
        1 -
        Math.floor(visiblePages / 2),
    );

    const end = Math.min(
      totalPages,
      start + visiblePages - 1,
    );

    if (end === totalPages) {
      start = Math.max(
        1,
        end - visiblePages + 1,
      );
    }

    return Array.from(
      {
        length: end - start + 1,
      },
      (_, index) => start + index,
    );
  };

  const pages = getPageNumbers();

  const handlePreviousPage = () => {
    if (isFirstPage) {
      return;
    }

    onPageChange(Math.max(currentPage - 1, 0));
  };

  const handleNextPage = () => {
    if (isLastPage || totalPages <= 0) {
      return;
    }

    onPageChange(
      Math.min(
        currentPage + 1,
        totalPages - 1,
      ),
    );
  };

  const pageButtonClass = (
    isActive: boolean,
  ) => `
    flex h-9 min-w-9 items-center
    justify-center border border-stroke
    px-2 text-sm font-medium
    transition-colors duration-150
    hover:z-10 hover:border-primary
    hover:bg-gray hover:text-primary
    dark:border-strokedark
    dark:hover:border-primary
    dark:hover:bg-graydark
    ${
      isActive
        ? `
          z-10 border-primary
          bg-primary text-white
          hover:bg-primary
          hover:text-white
          dark:border-primary
        `
        : `
          bg-white text-black
          dark:bg-boxdark dark:text-white
        `
    }
  `;

  return (
    <div
      className="
        flex flex-col gap-4
        border-t border-stroke
        px-4 pt-5
        dark:border-strokedark
        md:flex-row md:items-center
        md:justify-between md:px-8
      "
    >
      <p className="text-center font-medium text-black dark:text-white sm:text-left">
        Página{' '}
        {totalPages > 0
          ? currentPage + 1
          : 0}{' '}
        de {totalPages}

        {totalElements !== undefined && (
          <span className="text-body dark:text-bodydark2">
            {' '}
            · {totalElements} registros
          </span>
        )}
      </p>

      <div
        className="
          flex flex-col items-center
          gap-3
          sm:flex-row
        "
      >
        {onSizeChange && size !== undefined && (
          <PageSizeSelect
            value={size}
            onChange={onSizeChange}
          />
        )}

        {totalPages > 1 && (
        <nav
          aria-label="Navegación de páginas"
          className="
            flex max-w-full justify-center
            overflow-x-auto pb-1
            md:justify-end
          "
        >
          <ul className="flex items-center">
            <li>
              <button
                type="button"
                onClick={handlePreviousPage}
                disabled={isFirstPage}
                aria-label="Página anterior"
                className="
                  flex h-9 w-9 items-center
                  justify-center rounded-l-md
                  border border-stroke
                  bg-white text-black
                  transition-colors duration-150
                  hover:z-10 hover:border-primary
                  hover:bg-gray hover:text-primary
                  disabled:cursor-not-allowed
                  disabled:opacity-40
                  dark:border-strokedark
                  dark:bg-boxdark dark:text-white
                  dark:hover:border-primary
                  dark:hover:bg-graydark
                "
              >
                <svg
                  className="fill-current"
                  width="8"
                  height="16"
                  viewBox="0 0 8 16"
                  aria-hidden="true"
                >
                  <path d="M7.17578 15.1156C7.00703 15.1156 6.83828 15.0593 6.72578 14.9187L0.369531 8.44995C0.116406 8.19683 0.116406 7.80308 0.369531 7.54995L6.72578 1.0812C6.97891 0.828076 7.37266 0.828076 7.62578 1.0812C7.87891 1.33433 7.87891 1.72808 7.62578 1.9812L1.71953 7.99995L7.65391 14.0187C7.90703 14.2718 7.90703 14.6656 7.65391 14.9187C7.48516 15.0312 7.34453 15.1156 7.17578 15.1156Z" />
                </svg>
              </button>
            </li>

            {pages[0] > 1 && (
              <>
                <li>
                  <button
                    type="button"
                    onClick={() => onPageChange(0)}
                    aria-label="Ir a la página 1"
                    aria-current={
                      currentPage === 0
                        ? 'page'
                        : undefined
                    }
                    className={pageButtonClass(
                      currentPage === 0,
                    )}
                  >
                    1
                  </button>
                </li>

                {pages[0] > 2 && (
                  <li
                    aria-hidden="true"
                    className="
                      flex h-9 min-w-8
                      items-center justify-center
                      border-y border-stroke
                      bg-white px-1
                      text-body
                      dark:border-strokedark
                      dark:bg-boxdark
                      dark:text-bodydark2
                    "
                  >
                    …
                  </li>
                )}
              </>
            )}

            {pages.map((pageNumber) => {
              const pageIndex = pageNumber - 1;
              const isActive =
                currentPage === pageIndex;

              return (
                <li key={pageNumber}>
                  <button
                    type="button"
                    onClick={() =>
                      onPageChange(pageIndex)
                    }
                    aria-label={`Ir a la página ${pageNumber}`}
                    aria-current={
                      isActive
                        ? 'page'
                        : undefined
                    }
                    className={pageButtonClass(
                      isActive,
                    )}
                  >
                    {pageNumber}
                  </button>
                </li>
              );
            })}

            {pages[pages.length - 1] <
              totalPages && (
              <>
                {pages[pages.length - 1] <
                  totalPages - 1 && (
                  <li
                    aria-hidden="true"
                    className="
                      flex h-9 min-w-8
                      items-center justify-center
                      border-y border-stroke
                      bg-white px-1
                      text-body
                      dark:border-strokedark
                      dark:bg-boxdark
                      dark:text-bodydark2
                    "
                  >
                    …
                  </li>
                )}

                <li>
                  <button
                    type="button"
                    onClick={() =>
                      onPageChange(totalPages - 1)
                    }
                    aria-label={`Ir a la página ${totalPages}`}
                    aria-current={
                      currentPage ===
                      totalPages - 1
                        ? 'page'
                        : undefined
                    }
                    className={pageButtonClass(
                      currentPage ===
                        totalPages - 1,
                    )}
                  >
                    {totalPages}
                  </button>
                </li>
              </>
            )}

            <li>
              <button
                type="button"
                onClick={handleNextPage}
                disabled={isLastPage}
                aria-label="Página siguiente"
                className="
                  flex h-9 w-9 items-center
                  justify-center rounded-r-md
                  border border-stroke
                  bg-white text-black
                  transition-colors duration-150
                  hover:z-10 hover:border-primary
                  hover:bg-gray hover:text-primary
                  disabled:cursor-not-allowed
                  disabled:opacity-40
                  dark:border-strokedark
                  dark:bg-boxdark dark:text-white
                  dark:hover:border-primary
                  dark:hover:bg-graydark
                "
              >
                <svg
                  className="fill-current"
                  width="8"
                  height="16"
                  viewBox="0 0 8 16"
                  aria-hidden="true"
                >
                  <path d="M0.819531 15.1156C0.650781 15.1156 0.510156 15.0593 0.369531 14.9468C0.116406 14.6937 0.116406 14.3 0.369531 14.0468L6.27578 7.99995L0.369531 1.9812C0.116406 1.72808 0.116406 1.33433 0.369531 1.0812C0.622656 0.828076 1.01641 0.828076 1.26953 1.0812L7.62578 7.54995C7.87891 7.80308 7.87891 8.19683 7.62578 8.44995L1.26953 14.9187C1.15703 15.0312 0.988281 15.1156 0.819531 15.1156Z" />
                </svg>
              </button>
            </li>
          </ul>
        </nav>
        )}
      </div>
    </div>
  );
};

export default BasicPagination;